/** One relation of the "Where it's used" panel, in full: the records' relation drawer
 *  (`records/relations/RelationDrawer`, the tour's relations row) over the activity's
 *  used-by narrowed to the relation's type, a page at a time (`RELATION_PAGE_SIZE`), each
 *  row as the panel draws it. Never for the unlisted trips, which are counted, not listed. */
import { Box, CircularProgress, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import * as activitiesApi from '@/api/activities';
import {
  RELATION_PAGE_SIZE,
  RelationDrawer,
  RelationFailure,
  RelationPager,
} from '@/features/records/relations/RelationDrawer';

import { WhereUsedRow } from './WhereUsedRow';
import {
  whereUsedDrawerTitle,
  WHERE_USED_LABEL,
  WHERE_USED_NOUN,
  type WhereUsedGroupKey,
} from './whereUsedModel';

/** One page of one relation; under `['activities']`, so an activity write refreshes it. */
export const whereUsedPageKey = (activityId: number, group: WhereUsedGroupKey, page: number) =>
  ['activities', 'usedBy', activityId, group, page] as const;

export interface WhereUsedGroupDrawerProps {
  activityId: number;
  /** The activity's name as the app shows it (`activityTitle`). */
  activityName: string;
  group: WhereUsedGroupKey;
  open: boolean;
  onClose: () => void;
}

export function WhereUsedGroupDrawer({
  activityId,
  activityName,
  group,
  open,
  onClose,
}: WhereUsedGroupDrawerProps) {
  return (
    <RelationDrawer open={open} onClose={onClose} title={whereUsedDrawerTitle(group, activityName)}>
      {/* Mounted while open (and while it slides out); keyed by the relation, so another one
          starts on its first page. */}
      <GroupPages key={group} activityId={activityId} group={group} />
    </RelationDrawer>
  );
}

function GroupPages({ activityId, group }: { activityId: number; group: WhereUsedGroupKey }) {
  const [page, setPage] = useState(1);
  const noun = WHERE_USED_NOUN[group];
  const q = useQuery({
    queryKey: whereUsedPageKey(activityId, group, page),
    queryFn: () =>
      activitiesApi.usedBy(activityId, { type: group, page, pageSize: RELATION_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  if (q.isLoading) {
    return (
      <Typography
        role="status"
        variant="body2"
        color="text.secondary"
        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
      >
        <CircularProgress size={14} aria-hidden /> {`Loading the ${noun}…`}
      </Typography>
    );
  }
  if (q.isError || !q.data) {
    return <RelationFailure what={`the ${noun}`} onRetry={() => void q.refetch()} />;
  }
  const rows = q.data.results.filter((r) => r.type_key === group);
  return (
    <>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {`No ${noun} any more.`}
        </Typography>
      ) : (
        <Box
          component="ul"
          aria-label={WHERE_USED_LABEL[group]}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'grid',
            gap: 0.5,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {rows.map((row) => (
            <WhereUsedRow key={row.id} group={group} row={row} />
          ))}
        </Box>
      )}
      <RelationPager page={page} count={q.data.count} shown={rows.length} onPage={setPage} />
    </>
  );
}
